import {
  Award,
  BookOpen,
  BriefcaseBusiness,
  GraduationCap,
  Languages,
  Loader2,
  MapPin,
  ScrollText,
} from "lucide-react";

const TileEmployee = ({ data, t }) => {
  const jr = data?.json_response ?? {};
  const str = (v) => (typeof v === "string" ? v.trim() : "");

  if (jr.status === "running") {
    return (
      <div
        style={{ display: "flex", alignItems: "center", gap: 10, opacity: 0.5 }}
      >
        <Loader2 size={14} style={{ color: t.textSecondary }} />
        <p style={{ fontSize: 13, color: t.textSecondary, margin: 0 }}>
          Loading profile…
        </p>
      </div>
    );
  }

  const name = str(jr.name);
  const headline = str(jr.headline);
  const description = str(jr.description);
  const location = str(jr.location);
  const followers = str(jr.followers);
  const connections = str(jr.connections);
  const experiences = Array.isArray(jr.experience) ? jr.experience : [];
  const education = Array.isArray(jr.education) ? jr.education : [];
  const certs = Array.isArray(jr.certifications) ? jr.certifications : [];
  const publications = Array.isArray(jr.publications) ? jr.publications : [];
  const awards = Array.isArray(jr.honors_and_awards)
    ? jr.honors_and_awards
    : [];
  const languages = Array.isArray(jr.languages) ? jr.languages : [];

  const expItems = experiences.slice(0, 2);
  const eduItems =
    expItems.length === 0 ? education.slice(0, 2) : education.slice(0, 1);
  const certItems = certs.slice(0, 2);
  const pubItems = publications.slice(0, 2);
  const awardItems = awards.slice(0, 2);
  const langNames = languages.map((l) => str(l.name)).filter(Boolean);

  if (!name && !headline && expItems.length === 0) return null;

  const iconBox = {
    width: 30,
    height: 30,
    borderRadius: 8,
    background: t.iconBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };

  const pill = {
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 6,
    background: t.labelBg,
    border: `1px solid ${t.labelBorder}`,
    color: t.labelText,
    alignSelf: "flex-start",
    whiteSpace: "nowrap",
  };

  const icon = (Icon) => (
    <Icon size={14} style={{ color: t.textPrimary }} strokeWidth={1.5} />
  );

  const dateRange = (d) => {
    if (!d) return null;
    const s = d.start_date,
      e = d.end_date;
    if (!s && !e) return null;
    return `${s ?? ""}${s && e ? " – " : ""}${e ?? (s ? "present" : "")}`;
  };

  const IconRow = ({ icon: ic, primary, secondary, meta }) => (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        minWidth: 0,
      }}
    >
      <div style={{ ...iconBox, marginTop: 1 }}>{ic}</div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          minWidth: 0,
          flex: 1,
        }}
      >
        {primary && (
          <p
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: t.textPrimary,
              margin: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {primary}
          </p>
        )}
        {secondary && (
          <p
            style={{
              fontSize: 13,
              color: t.textPrimary,
              margin: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              opacity: 0.7,
            }}
          >
            {secondary}
          </p>
        )}
        {meta && <span style={pill}>{meta}</span>}
      </div>
    </div>
  );

  // A labeled section block with its own border and breathing room
  const Section = ({ label, children }) => (
    <div
      style={{
        marginTop: 4,
        paddingTop: 14,
        borderTop: `1px solid ${t.divider}`,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <p
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: t.textPrimary,
          opacity: 0.35,
          margin: 0,
        }}
      >
        {label}
      </p>
      {children}
    </div>
  );

  return (
    <div style={{ minWidth: 0, overflow: "hidden" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {name && (
          <p
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: t.textPrimary,
              margin: 0,
              lineHeight: 1.4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </p>
        )}

        {headline && (
          <p
            style={{
              fontSize: 13,
              color: t.textPrimary,
              margin: 0,
              lineHeight: 1.6,
              opacity: 0.8,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {headline}
          </p>
        )}

        {description && (
          <p
            style={{
              fontSize: 13,
              color: t.textPrimary,
              opacity: 0.75,
              margin: 0,
              lineHeight: 1.6,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {description}
          </p>
        )}

        {(location || followers || connections) && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
              paddingTop: 12,
              borderTop: `1px solid ${t.divider}`,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {location && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <MapPin
                  size={11}
                  style={{ color: t.textPrimary, flexShrink: 0, opacity: 0.7 }}
                  strokeWidth={1.5}
                />
                <span
                  style={{
                    fontSize: 12,
                    color: t.textPrimary,
                    opacity: 0.7,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {location}
                </span>
              </div>
            )}
            {(followers || connections) && (
              <span
                style={{
                  fontSize: 12,
                  color: t.textPrimary,
                  opacity: 0.7,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {[followers, connections].filter(Boolean).join(" · ")}
              </span>
            )}
          </div>
        )}

        {expItems.length > 0 && (
          <Section label="Experience">
            {expItems.map((item, i) => (
              <IconRow
                key={i}
                icon={icon(BriefcaseBusiness)}
                primary={
                  item.title ||
                  [item.target, item.location].filter(Boolean).join(", ") ||
                  null
                }
                secondary={
                  item.title
                    ? [item.target, item.location].filter(Boolean).join(", ") ||
                      null
                    : null
                }
                meta={dateRange(item.date)}
              />
            ))}
          </Section>
        )}

        {eduItems.length > 0 && (
          <Section label="Education">
            {eduItems.map((item, i) => (
              <IconRow
                key={i}
                icon={icon(GraduationCap)}
                primary={item.university_name || null}
                secondary={
                  [item.degree, item.major].filter(Boolean).join(", ") || null
                }
                meta={dateRange(item.date)}
              />
            ))}
          </Section>
        )}

        {certItems.length > 0 && (
          <Section label="Certifications">
            {certItems.map((item, i) => (
              <IconRow
                key={i}
                icon={icon(ScrollText)}
                primary={str(item.title) || null}
                secondary={str(item.issuer) || null}
                meta={str(item.issued_date) || null}
              />
            ))}
          </Section>
        )}

        {pubItems.length > 0 && (
          <Section label="Publications">
            {pubItems.map((item, i) => (
              <IconRow
                key={i}
                icon={icon(BookOpen)}
                primary={str(item.title) || null}
                secondary={str(item.publisher) || null}
                meta={str(item.publication_date) || null}
              />
            ))}
          </Section>
        )}

        {awardItems.length > 0 && (
          <Section label="Awards">
            {awardItems.map((item, i) => (
              <IconRow
                key={i}
                icon={icon(Award)}
                primary={str(item.title) || null}
                secondary={str(item.issuer) || null}
                meta={str(item.issued_date) || null}
              />
            ))}
          </Section>
        )}

        {langNames.length > 0 && (
          <Section label="Languages">
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Languages
                size={13}
                style={{ color: t.textPrimary, flexShrink: 0 }}
                strokeWidth={1.5}
              />
              {langNames.map((lang, i) => (
                <span key={i} style={pill}>
                  {lang}
                </span>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
};

export default TileEmployee;
